import { z } from 'zod';
export const PropuestaPagoLineaInputSchema = z.object({
    entryNo: z.number().int().positive(),
    vendorCode: z.string().min(1),
    montoAPagar: z.number().positive(),
    montoAPagarLCY: z.number().nonnegative(),
    currencyCode: z.string().nullable().optional(),
    cuentaBancoOrigen: z.string().nullable().optional(),
    notas: z.string().max(500).nullable().optional(),
});
export const MarcarProveedorPactadoSchema = z.object({
    motivo: z.string().trim().max(200).nullable().optional(),
});
export const SaldoCuentaInputSchema = z.object({
    cuentaBancoOrigen: z.string().min(1),
    saldoDisponible: z.number(),
});
export const TipoPropuestaPagoSchema = z.enum(['CREDITO', 'CONTADO']);
export const CrearPropuestaPagoSchema = z.object({
    nombre: z.string().min(1).max(200),
    tipo: TipoPropuestaPagoSchema.default('CREDITO'),
    fechaPagoPropuesta: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    lineas: z.array(PropuestaPagoLineaInputSchema).min(1),
    saldosCuentas: z.array(SaldoCuentaInputSchema).optional(),
});
export const ActualizarPropuestaPagoSchema = z.object({
    nombre: z.string().min(1).max(200).optional(),
    fechaPagoPropuesta: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    lineas: z.array(PropuestaPagoLineaInputSchema).min(1).optional(),
    saldosCuentas: z.array(SaldoCuentaInputSchema).optional(),
});
/** Anotar a qué factura se aplicó realmente el pago de una línea (al menos la factura o una nota). */
export const AplicacionLineaSchema = z
    .object({
    aplicadaADocumento: z.string().trim().max(30).nullish(),
    nota: z.string().trim().max(300).nullish(),
})
    .refine((d) => !!d.aplicadaADocumento || !!d.nota, {
    message: 'Indicá la factura a la que se aplicó o una nota',
});
export const EnviarABCSchema = z.object({
    cuentaBancoOrigen: z.string().min(1).max(40),
    journalTemplate: z.string().min(1).max(10),
    journalBatch: z.string().min(1).max(10),
    /** Solo cuentas sin archivo de banco: fecha de registro del diario (AAAA-MM-DD). */
    postingDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    /** Confirmar a sabiendas que el diario ya trae líneas (de Contabilidad u otro envío). */
    confirmarDiarioConLineas: z.boolean().optional(),
    /** Enviar sin los proveedores con facturas cerradas o con menos saldo en BC (se registran a mano). */
    omitirProveedoresConCambios: z.boolean().optional(),
});
export const CrearSolicitudContadoSchema = z
    .object({
    fecha: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
    vendorCode: z.string().min(1).max(20).nullable().optional(),
    beneficiario: z.string().trim().min(1).max(200).nullable().optional(),
    currencyCode: z.string().max(10).nullable().optional(),
    tipoDocumento: z.enum(['ORDEN', 'FACTURA', 'MIXTO', 'OTRO']),
    /** Para tipo ORDEN o MIXTO: una o varias OC (`CP-006260`, `OC6260` o `6260`). */
    documentosBC: z.array(z.string().trim().min(1).max(50)).max(30).optional(),
    /** Para tipo FACTURA o MIXTO: una o varias facturas abiertas del proveedor (movimientos de BC). */
    entryNos: z.array(z.number().int().positive()).max(30).optional(),
    referencia: z.string().trim().max(100).nullable().optional(),
    descripcion: z.string().trim().min(1).max(300),
    montoSolicitado: z.number().positive(),
})
    // En una solicitud de factura el proveedor sale de la factura elegida.
    .refine((d) => d.vendorCode ||
    d.beneficiario ||
    ((d.tipoDocumento === 'FACTURA' || d.tipoDocumento === 'MIXTO') && d.entryNos?.length), {
    message: 'Indicá el proveedor de BC o el nombre del beneficiario',
    path: ['vendorCode'],
})
    .refine((d) => d.tipoDocumento !== 'ORDEN' || (d.documentosBC?.length ?? 0) > 0, {
    message: 'Una solicitud de orden de compra necesita al menos una OC',
    path: ['documentosBC'],
})
    .refine((d) => d.tipoDocumento !== 'MIXTO' || (d.documentosBC?.length ?? 0) + (d.entryNos?.length ?? 0) > 0, {
    message: 'Elegí al menos una OC o una factura',
    path: ['documentosBC'],
})
    .refine((d) => d.tipoDocumento !== 'FACTURA' || (d.entryNos?.length ?? 0) > 0, {
    message: 'Una solicitud de factura necesita elegir al menos una factura',
    path: ['entryNos'],
});
export const ActualizarSolicitudContadoSchema = z.object({
    referencia: z.string().trim().max(100).nullable().optional(),
    descripcion: z.string().trim().min(1).max(300).optional(),
    montoSolicitado: z.number().positive().optional(),
});
export const AprobarSolicitudContadoSchema = z.object({
    /** Si no viene, se aprueba el monto solicitado. */
    montoAprobado: z.number().positive().optional(),
    /** OC que se pagan (solo solicitudes de OC). Las demás pasan a una solicitud nueva pendiente. Si no viene, todas. */
    documentosAprobados: z.array(z.string().min(1).max(50)).max(30).optional(),
    /** Si se aprueba menos de lo solicitado, la diferencia queda como una solicitud nueva pendiente (por defecto sí). */
    dejarSaldoPendiente: z.boolean().optional(),
});
export const RechazarSolicitudContadoSchema = z.object({
    motivo: z.string().trim().max(300).nullable().optional(),
});
const FechaISOSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Cuenta del proveedor elegida a mano: código de proveedor → id de su cuenta. */
export const CuentasElegidasSchema = z.record(z.string(), z.number().int().positive());
export const GenerarArchivoContadoSchema = z.object({
    cuentaBancoOrigen: z.string().min(1).max(40),
    /** Solicitudes aprobadas que se pagan (una línea por proveedor en el archivo). */
    solicitudIds: z.array(z.number().int().positive()).min(1).max(500),
    fecha: FechaISOSchema.optional(),
    /** BAC: plan (BOK) y número de envío. */
    planCode: z.string().trim().min(1).max(20).optional(),
    envioNo: z.number().int().positive().max(999).optional(),
    /** BN: número de transferencia. */
    transferenciaNo: z.number().int().min(1).max(99999).optional(),
    /** Cuenta del proveedor elegida a mano (código de proveedor → id de su cuenta). */
    cuentasElegidas: CuentasElegidasSchema.optional(),
});
export const PagarContadoAManoSchema = z.object({
    solicitudIds: z.array(z.number().int().positive()).min(1).max(500),
    /** Cómo se pagó (SINPE móvil, transferencias una a una, comprobantes…). */
    referencia: z.string().trim().max(200).nullable().optional(),
    /** Día en que se hizo el pago (AAAA-MM-DD); si no viene, hoy. Decide en qué semana cuenta. */
    fechaPago: FechaISOSchema.optional(),
});
/** Marca toda la lista como pagada (un registro por moneda). */
export const MarcarListaPagadaSchema = z.object({
    referencia: z.string().trim().max(200).nullable().optional(),
    fechaPago: FechaISOSchema.optional(),
});
/** Corrige el día en que se hizo un pago ya registrado. */
export const CambiarFechaPagoContadoSchema = z.object({
    fecha: FechaISOSchema,
});
//# sourceMappingURL=propuestaPago.schema.js.map